import { ApiConflictError, apiCall } from '@/lib/api-client';
import type { DispatchChildStatus, DispatchData } from '@/features/agent-dispatch/types';
import type { EuProcedure } from './eu-procedures';

/**
 * Regulatory Strategy run dispatch + polling API. Mirrors the predicate
 * finder pattern but with a distinct response envelope: dispatch returns
 * `strategy_run_id` and the run-detail endpoint inlines the parsed
 * `strategy_response` payload directly (vs predicate's raw output_text).
 */

export type AgentRunStatus = 'queued' | 'running' | 'completed' | 'failed';

/** One per-country child inside the dispatch response. */
export interface StrategyDispatchChild {
  id: number;
  country: string | null;
  status: DispatchChildStatus;
}

export interface StrategyDispatchResponse {
  /** First child run's id — kept for backwards-compat navigation. */
  strategy_run_id: number;
  /** Shared across every sibling in this dispatch. */
  dispatch_group_id: string;
  /** Per-country child summaries (id + country + status). */
  runs: StrategyDispatchChild[];
  status: string;
  agent_kind: string;
  country: string;
  product_id: string;
  product_name: string;
  product_type: string;
  poll_ms: number;
  max_polls: number;
}

/**
 * Dispatch a strategy run for one or more countries. ``countries`` is sent
 * as a comma-joined list (e.g. ``"US,CA,EU"``) — the backend writes N
 * sibling rows in ``strategy_agent_runs``, all sharing one
 * ``dispatch_group_id``, and returns the first child's id as
 * ``strategy_run_id`` for navigation plus the full ``runs[]`` roster for
 * the multi-country tabbed view.
 *
 * ``procedure`` + ``memberStates`` are EU-only (Phase E.1.5). They're
 * silently ignored for non-EU dispatches by the backend, so they can be
 * passed unconditionally as long as ``EU`` may be in the country set.
 *
 * 409 conflict (already-in-flight run for this product + any requested
 * country) surfaces as ``ApiConflictError`` with ``detail.existing_run_id``
 * so the caller can navigate to the in-flight run's result page.
 */
export async function dispatchStrategyRun(
  productId: number,
  countries: string | readonly string[],
  procedure?: EuProcedure,
  memberStates?: readonly string[],
  rms?: string,
  authNumber?: string,
): Promise<StrategyDispatchResponse> {
  const country = typeof countries === 'string'
    ? countries
    : countries.map((c) => c.toUpperCase()).join(',');
  const params = new URLSearchParams({ country });
  if (procedure) {
    params.set('procedure', procedure);
  }
  if (memberStates && memberStates.length > 0) {
    params.set(
      'member_states',
      memberStates.map((c) => c.toUpperCase()).join(','),
    );
  }
  if (rms) {
    params.set('rms', rms.toUpperCase());
  }
  if (authNumber && authNumber.trim()) {
    params.set('auth_number', authNumber.trim());
  }
  return apiCall<StrategyDispatchResponse>(
    `/api/products/${productId}/strategy?${params.toString()}`,
    { method: 'POST' },
  );
}

/**
 * Extract `existing_run_id` from a 409 conflict, if present.
 */
export function existingStrategyRunIdFromConflict(err: unknown): number | null {
  if (!(err instanceof ApiConflictError)) return null;
  const detail = err.detail as { detail?: { existing_run_id?: unknown } } | undefined;
  const id = detail?.detail?.existing_run_id;
  return typeof id === 'number' ? id : null;
}

/**
 * Extract `locked_countries` from the re-run-gate 409 (see
 * ``generate_strategy`` in ``backend/routers/products.py``), if present. This
 * is a distinct 409 shape from the in-flight-run conflict above — it carries
 * no ``existing_run_id`` — so callers should check this only after
 * ``existingStrategyRunIdFromConflict`` returns null.
 */
export function lockedCountriesFromConflict(err: unknown): string[] | null {
  if (!(err instanceof ApiConflictError)) return null;
  const detail = err.detail as { detail?: { locked_countries?: unknown } } | undefined;
  const codes = detail?.detail?.locked_countries;
  if (!Array.isArray(codes)) return null;
  const filtered = codes.filter((c): c is string => typeof c === 'string');
  return filtered.length > 0 ? filtered : null;
}

/** Phase E.1.7 — user-picked EU pathway, surfaced by the run GET so the
 *  result page can render "Your dispatch" alongside the agent's
 *  ``recommendedPathway`` and flag a mismatch. ``null`` for non-EU runs
 *  and legacy EU rows without a procedure picked.
 *
 *  Phase E.1.8 — devices use this shape with no `procedure` (target-
 *  markets only mode). Drug/biologic runs include `procedure`. */
export interface RunPathway {
  /** Optional — present for drug/biologic runs, absent for device target-markets runs. */
  procedure?: EuProcedure;
  member_states: string[];
  rms?: string | null;
  auth_number?: string | null;
}

/**
 * One strategy run row (polling shape). `strategy_response` is the parsed
 * `RegulatoryStrategy` envelope — only present when `status === 'completed'`.
 */
export interface StrategyRunDetail {
  id: number;
  /** Customer-facing "Run #N" — per (product, agent). Null on legacy pre-backfill rows. */
  run_number?: number | null;
  product_id: number;
  /** Plucked out of ``Product.data["product_name"]`` by the GET handler
   *  so the shared ``AgentRunCard`` meta strip can label which product
   *  the run is for. ``null`` when missing. */
  product_name: string | null;
  agent_kind: string;
  country: string | null;
  status: AgentRunStatus;
  /** Non-null when this run was created via the multi-country dispatch
   *  path — frontend reads this to switch the result page to the tabbed
   *  view. Same pattern as predicate + dossier. */
  dispatch_group_id: string | null;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
  strategy_response: StrategyResponse | null;
  output_metadata: Record<string, unknown> | null;
  pathway: RunPathway | null;
  poll_ms: number;
  max_polls: number;
}

/**
 * Top-level response envelope serialized by `execute_strategy_run` and
 * inlined under `StrategyRunDetail.strategy_response`.
 *
 *   product_id, product_name, product_type — for the result-page header
 *   model, iterations                     — for the metadata line
 *   strategy                              — the RegulatoryStrategy payload
 *   tool_calls                            — FDA tool calls the agent made
 *   warnings                              — non-fatal notes
 */
export interface StrategyResponse {
  product_id: string;
  product_name: string;
  product_type: string;
  model: string;
  iterations: number;
  strategy: RegulatoryStrategy;
  tool_calls: StrategyToolCall[];
  warnings: string[];
  strategy_run_id: number;
  [extra: string]: unknown;
}

/**
 * The structured strategy envelope (mirrors `RegulatoryStrategy` in
 * `app/schemas.py`). The agent emits a stable shape plus arbitrary extras
 * that the renderer surfaces generically via the humanize-key pattern.
 */
export interface RegulatoryStrategy {
  productType?: string;
  executiveSummary?: StrategyExecutiveSummary;
  pathwayAnalysis?: Record<string, unknown> | null;
  evidenceRequirements?: Record<string, unknown> | null;
  qualityAndManufacturing?: Record<string, unknown> | null;
  fdaEngagement?: Record<string, unknown> | null;
  timeline?: StrategyMilestone[];
  riskRegister?: StrategyRisk[];
  recommendations?: string[];
  gapsAndAssumptions?: string[];
  sources?: StrategySource[];
  deviceSpecifics?: Record<string, unknown> | null;
  drugSpecifics?: Record<string, unknown> | null;
  [extra: string]: unknown;
}

export interface StrategyToolCall {
  tool: string;
  arguments: Record<string, unknown>;
  ok: boolean;
  summary?: string | null;
}

export interface StrategyExecutiveSummary {
  recommendedPathway?: string | null;
  proposedClassification?: string | null;
  clinicalDataRequired?: string | null;
  targetTimeframe?: string | null;
  /** US devices only; "Recommended" or "N/A — <reason>", set by the backend PCCP gate. */
  pccp?: string | null;
  topRisks?: string[];
  summary?: string | null;
  [extra: string]: unknown;
}

/** The PCCP block under pathwayAnalysis; present only when the gate found the pathway eligible. */
export interface StrategyPccp {
  recommendation?: string | null;
  candidateModifications?: string[];
  guidance?: {
    docket?: string | null;
    title?: string | null;
    status?: string | null;
    issueDate?: string | null;
  } | null;
  [extra: string]: unknown;
}

export interface StrategyMilestone {
  milestone: string;
  timeframe?: string | null;
  dependencies?: string[];
  [extra: string]: unknown;
}

export interface StrategyRisk {
  risk: string;
  impact?: string | null;
  mitigation?: string | null;
  [extra: string]: unknown;
}

export interface StrategySource {
  title?: string | null;
  identifier?: string | null;
  url?: string | null;
  note?: string | null;
  [extra: string]: unknown;
}

export async function getStrategyRun(runId: number): Promise<StrategyRunDetail> {
  return apiCall<StrategyRunDetail>(`/api/agents/strategy/runs/${runId}`, {
    method: 'GET',
  });
}

/** Fetch every sibling run in a multi-country strategy dispatch group.
 *  Returns the same generic shape predicate + dossier use so the shared
 *  ``<DispatchTabsShell>`` consumes it without per-agent special-casing. */
export async function getStrategyDispatchDetail(
  groupId: string,
): Promise<DispatchData> {
  return apiCall<DispatchData>(
    `/api/agents/strategy/dispatch/${groupId}`,
    { method: 'GET' },
  );
}

/** The settings a locked market's strategy was generated with (EU only). */
export interface StrategyLockedInputs {
  procedure: string | null;
  member_states: string[] | null;
  rms: string | null;
  auth_number: string | null;
}

/** One market's dispatch availability, as reported by the freshness check. */
export interface MarketAvailabilityItem {
  country: string;
  available: boolean;
  reason: 'never_run' | 'product_changed' | 'up_to_date' | 'market_disabled';
  latest_run_id: number | null;
  locked_inputs: StrategyLockedInputs | null;
}

export interface MarketAvailability {
  markets: MarketAvailabilityItem[];
  /** 'none' — no verdict yet, but one is coming (poll for it) · 'no_baseline' —
   *  the product has no snapshot-bearing run to compare against, so no verdict
   *  will ever come (do NOT poll) · 'pending' — check in flight · 'ready' —
   *  decided. */
  report_status: 'none' | 'no_baseline' | 'pending' | 'ready';
}

export async function getMarketAvailability(
  productId: number,
): Promise<MarketAvailability> {
  return apiCall<MarketAvailability>(
    `/api/products/${productId}/market-availability`,
  );
}

/** Start the freshness check. Fire-and-forget; the picker polls for the result. */
export async function triggerMaterialityCheck(productId: number): Promise<void> {
  await apiCall<{ scheduled: boolean }>(
    `/api/products/${productId}/materiality-check`,
    { method: 'POST' },
  );
}
